import React, { useMemo, useState } from 'react';
import { View, StyleSheet, Image, ActivityIndicator, Platform, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import MessageContentRenderer from '@/components/Messages/MessageContentRenderer';
import MessageExpandable from '@/components/Messages/MessageExpandable';
import { MESSAGE_LAYOUT } from '@/constants/config';
import { useToolStore, useToastStore, useModalStore, useAuthStore } from '@/store';
import Button from '@/components/Buttons/Button';
import PlusOutOfCreditsOptions from '@/components/Credits/PlusOutOfCreditsOptions';
import { isInsufficientCreditsError } from '@/lib/plusCreditOptions';
// Real, interactive 3D viewer (model-viewer / three.js) instead of the
// placeholder cube illustration that ToolResultDisplay used to render.
import Model3DViewer from '@/components/Model3D/Model3DViewer';
import PointCloudViewer from '@/components/Tools/AdvancedComponents/PointCloudViewer';
import LayerViewer from '@/components/Tools/AdvancedComponents/LayerViewer';
// Same waveform-with-controls audio player chat messages use. Cross-platform
// via expo-audio so the music tool's MP3 / WAV results are playable inline
// on iOS and Android instead of falling through to a generic file tile.
import AudioWaveformPlayer from '@/components/ChatInput/AudioWaveformPlayer';
import { downloadMedia, downloadAndShare } from '@/constants/utils/mediaDownload';
import { toolRunPlainTextForCopy, resolveToolImagePreviewAspectRatio } from '@/components/Tools/toolDetailHelpers';
import ToolModelRetry from '@/components/Tools/ToolModelRetry';

// Real touch device — drives native-only affordances like the OS share sheet
// chip and a "Save to Photos" relabel of the download chip for image/video
// results (clearer wording than "Download" on iOS / Android).
const IS_TOUCH_NATIVE = Platform.OS === 'ios' || Platform.OS === 'android';

// Long text tool outputs (blog posts, outlines, etc.) collapse like chat messages.
const TOOL_TEXT_COLLAPSED_MAX_HEIGHT = 280;

// Same width / overflow contract as `MessageContent.jsx` so markdown (code
// fences, tables, math) lays out identically to assistant messages in chat.
const resultMarkdownContainerStyle = Platform.select({
  web: { minWidth: 0, flexShrink: 1, maxWidth: '100%', overflow: 'hidden' },
  default: { minWidth: 0, flexShrink: 0 },
});

/**
 * Renders a completed/failed ToolRun result. The status header lives in the
 * parent section header (slug page) so this component intentionally has no
 * inner "Result"/status row of its own. Actions:
 *  - Continue in chat (promote the backing conversation)
 *  - Copy result (text)
 *  - Edit & re-run (parent supplies onEditRerun)
 *
 * The action row is rendered on both top and bottom of the result body so
 * the user never has to scroll past long output to act on it.
 */
function ToolResultDisplay({ run, onEditRerun, tool, onRetryWithModel, isRetryDisabled }) {
  const { colors, theme: themeName } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const promoteRunToConversation = useToolStore(s => s.promoteRunToConversation);
  const { addToast } = useToastStore(s => s.actions);
  const { openModal } = useModalStore(state => state.actions);
  const [isPromoting, setIsPromoting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  // Native share is its own state slot (vs. piggybacking `isDownloading`)
  // so the user can see at-a-glance which chip is mid-flight when both are
  // visible. Web ignores this entirely — no Share chip is rendered.
  const [isSharing, setIsSharing] = useState(false);

  const isFailed = run?.status === 'failed';
  const isCreditFailure = isFailed && isInsufficientCreditsError(run?.error);
  const isPlus = useAuthStore(s => !!s.user?.flags?.isPlus);
  const isRunning = run?.status === 'running' || run?.status === 'pending';
  const resultFiles = Array.isArray(run?.resultFiles) ? run.resultFiles : [];
  const resultText = run?.resultText || '';
  const resultSegments = Array.isArray(run?.resultSegments) ? run.resultSegments : [];
  const hasResultSegments = resultSegments.length > 0;
  // Files that actually have a fetchable URL — used to decide whether the
  // Download chip should be rendered at all (no point offering it when the
  // run only produced text).
  const downloadableFiles = useMemo(
    () => resultFiles.filter(f => !!fileUrlOf(f)),
    [resultFiles]
  );

  // CRITICAL: every hook in this component MUST live above the
  // `if (isRunning) return null` guard further down. When the run flips
  // running → completed, an early-returned render has fewer hooks than the
  // post-flip render — and React aborts with "Rendered more hooks than
  // during the previous render", which the global ErrorBoundary then
  // catches with the generic "Something went wrong" screen. (See the
  // existing comment near the early-return for the matching warning.)
  //
  // Used to relabel the download chip ("Save to Photos") on iOS / Android
  // when the run actually produced media that lands in the Photos app.
  const hasPhotoLibraryMedia = useMemo(() => downloadableFiles.some(f => {
    const mime = (f?.mimeType || '').toLowerCase();
    return mime.startsWith('image/') || mime.startsWith('video/');
  }), [downloadableFiles]);

  const rendererTheme = useMemo(
    () => ({ colors, theme: themeName }),
    [colors, themeName],
  );

  const backingConversationId = useMemo(() => {
    const c = run?.conversation;
    if (c == null || c === '') return undefined;
    return String(c._id || c.id || c);
  }, [run?.conversation]);

  const runMessageId = run?.id != null ? String(run.id) : undefined;

  const copyPlainText = useMemo(() => toolRunPlainTextForCopy(run), [run]);

  const styles = useMemo(() => StyleSheet.create({
    container: {
      width: '100%',
      borderRadius: 12,
      borderWidth: 1,
      borderColor: isFailed && !isCreditFailure ? colors.color_destructive : colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      padding: 16,
      gap: 12,
    },
    body: {},
    segmentCard: {
      width: '100%',
      borderRadius: 10,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_tertiary,
      padding: 12,
      gap: 8,
    },
    segmentHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      paddingBottom: 6,
      marginBottom: 4,
      borderBottomWidth: 1,
      borderBottomColor: colors.border_color_secondary,
    },
    segmentsColumn: {
      width: '100%',
      gap: 14,
    },
    fileGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
      width: '100%',
    },
    imagePreviewFrame: {
      width: '100%',
      maxWidth: 280,
      borderRadius: 8,
      overflow: 'hidden',
      backgroundColor: colors.solid_color_secondary,
    },
    image: {
      width: '100%',
      height: '100%',
      borderRadius: 8,
      backgroundColor: colors.solid_color_secondary,
    },
    fileTile: {
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_tertiary,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      maxWidth: '100%',
    },
    // Wraps any media preview (image / 3D / etc.) so we can show a small
    // filename caption underneath without inheriting whatever padding the
    // viewer brings.
    mediaItem: {
      gap: 6,
      maxWidth: '100%',
    },
    mediaCaption: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      maxWidth: '100%',
    },
    mediaCaptionIcon: {
      flexShrink: 0,
    },
    mediaCaptionText: {
      flexShrink: 1,
      minWidth: 0,
    },
    imageThumbWrap: {
      width: '100%',
    },
    mediaPreview: {
      width: '100%',
      minWidth: 260,
      borderRadius: 10,
      overflow: 'hidden',
      backgroundColor: colors.solid_color_tertiary,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      padding: 10,
      gap: 8,
    },
    // Native audio player surface. AudioWaveformPlayer renders its own SVG
    // waveform + transport controls and is sized via its `size` prop, so the
    // wrapper just needs to provide the framed background + a fixed-ish
    // height so the waveform has room to breathe. Slightly taller than the
    // chat-message variant since tool results live in a dedicated panel.
    audioPlayerWrap: {
      width: '100%',
      minWidth: 260,
      height: 96,
      borderRadius: 10,
      overflow: 'hidden',
      backgroundColor: colors.solid_color_tertiary,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      paddingHorizontal: 6,
      paddingVertical: 6,
      justifyContent: 'center',
    },
    // Model3DViewer fills its parent (width:100% / height:100%), so the
    // viewport must be explicitly sized here or the iframe collapses to 0.
    modelFrame: {
      width: '100%',
      minWidth: 280,
      height: 360,
      borderRadius: 12,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_tertiary,
    },
    actions: {
      flexDirection: 'row',
      gap: 6,
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    actionsTop: {
      paddingBottom: 10,
      borderBottomWidth: 1,
      borderBottomColor: colors.border_color_secondary,
    },
    actionsBottom: {
      paddingTop: 10,
      borderTopWidth: 1,
      borderTopColor: colors.border_color_secondary,
    },
    chip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: 'transparent',
      ...(Platform.OS === 'web' && {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
      }),
    },
    chipHovered: {
      backgroundColor: colors.solid_color_secondary,
      borderColor: colors.text_color_tertiary,
    },
    chipPrimary: {
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_primary,
    },
    chipPrimaryHovered: {
      backgroundColor: colors.solid_color_tertiary,
      borderColor: colors.text_color_primary,
    },
    chipDisabled: {
      opacity: 0.55,
      ...(Platform.OS === 'web' && { cursor: 'not-allowed' }),
    },
    actionsSpacer: {
      flexGrow: 1,
    },
    meta: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 8,
    },
    errorBox: {
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderRadius: 8,
      backgroundColor: colors.solid_color_tertiary,
    },
    creditErrorBox: {
      gap: 12,
    },
    creditErrorHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },
    creditErrorIcon: {
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.accent_color_primary + '15',
    },
    runningRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
  }), [colors, isFailed, isCreditFailure]);

  // While the run is in flight the parent already renders <ToolProgressIndicator />
  // (which has its own poll loop, status row, and pipeline strip). Bailing
  // here avoids stacking a second "Running…" row underneath it.
  //
  // IMPORTANT: this early return MUST come after every hook call above. The
  // previous version returned before useMemo, which meant React saw a
  // different number of hooks across renders the moment the run flipped from
  // running → completed and threw "Rendered more hooks than during the
  // previous render." (a.k.a. the "Too many renders" toast).
  if (isRunning) return null;

  const handleContinueInChat = async () => {
    if (!run?.id || isPromoting) return;
    try {
      setIsPromoting(true);
      const conversation = await promoteRunToConversation(run.id);
      if (conversation?.id) {
        router.push(`/c/${conversation.id}`);
      }
    } catch (e) {
      addToast(e?.message || t('pages.tools.couldNotContinueInChat') || 'Could not continue in chat', 'error');
    } finally {
      setIsPromoting(false);
    }
  };

  // Open the first attached output in the shared FileEditModal — same modal
  // chat / collections / sidebar use, so users get the full preview + rename
  // + download / copy URL surface they're already familiar with instead of
  // ejecting them to a raw signed URL in a new tab.
  const handleOpen = () => {
    const file = downloadableFiles[0];
    const fileId = file?.id || file?._id || file?.fileId;
    if (!fileId) return;
    openModal('FileEditModal', { fileId: String(fileId) });
  };

  // (`hasPhotoLibraryMedia` is computed at the top of the component, above
  // the early-return guard, to keep the hook order stable. See the comment
  // on the early-return for why moving any useMemo below it is unsafe.)

  // Native share. We share only the *primary* (first) downloadable file —
  // multi-file native share is gnarly across iOS / Android and most users
  // share one image at a time anyway. The "Download" chip handles bulk.
  const handleShare = async () => {
    if (isSharing || !downloadableFiles.length) return;
    const file = downloadableFiles[0];
    const url = fileUrlOf(file);
    if (!url) return;
    setIsSharing(true);
    try {
      const slugBase = run?.tool?.slug || run?.toolSlug || 'gab-ai';
      const baseName = (file?.originalName || file?.name)
        ? String(file.originalName || file.name).replace(/\.[a-zA-Z0-9]+$/, '')
        : `${slugBase}-${run.id}`;
      await downloadAndShare(url, baseName, {
        onError: (msg) => addToast(msg || (t('pages.tools.shareFailed') || 'Share failed'), 'error'),
      });
    } catch (e) {
      addToast(e?.message || t('pages.tools.shareFailed') || 'Share failed', 'error');
    } finally {
      setIsSharing(false);
    }
  };

  const handleDownload = async () => {
    if (isDownloading || !downloadableFiles.length) return;
    setIsDownloading(true);
    try {
      const slugBase = run?.tool?.slug || run?.toolSlug || 'gab-ai';
      // Sequentially download each file. We give the browser a small breath
      // between clicks so multiple anchor-driven downloads aren't dropped on
      // platforms that throttle bursts (Chrome, Safari).
      for (let i = 0; i < downloadableFiles.length; i++) {
        const file = downloadableFiles[i];
        const url = fileUrlOf(file);
        if (!url) continue;
        const baseName = (file?.originalName || file?.name)
          ? String(file.originalName || file.name).replace(/\.[a-zA-Z0-9]+$/, '')
          : `${slugBase}-${run.id}${downloadableFiles.length > 1 ? `-${i + 1}` : ''}`;
        await downloadMedia(url, baseName, {
          onError: (msg) => addToast(msg || (t('pages.tools.downloadFailed') || 'Download failed'), 'error'),
        });
        if (i < downloadableFiles.length - 1) {
          await new Promise(r => setTimeout(r, 250));
        }
      }
    } catch (e) {
      addToast(e?.message || t('pages.tools.downloadFailed') || 'Download failed', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopy = async () => {
    if (!copyPlainText?.trim()) return;
    try {
      if (Platform.OS === 'web' && navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(copyPlainText);
      } else {
        const Clipboard = require('expo-clipboard');
        await Clipboard.setStringAsync(copyPlainText);
      }
      addToast(t('pages.tools.copied') || 'Copied to clipboard', 'success');
    } catch (e) {
      addToast(t('pages.tools.copyFailed') || 'Could not copy', 'error');
    }
  };

  const renderToolTextOutput = (content, messageId, fadeColor) => (
    <MessageExpandable
      content={content}
      fadeColor={fadeColor}
      collapsedMaxHeight={TOOL_TEXT_COLLAPSED_MAX_HEIGHT}
    >
      <View style={resultMarkdownContainerStyle}>
        <MessageContentRenderer
          content={content}
          theme={rendererTheme}
          isStreaming={false}
          messageLayout={MESSAGE_LAYOUT.WIDE}
          isUser={false}
          isError={false}
          isDeleted={false}
          conversationId={backingConversationId}
          messageId={messageId}
        />
      </View>
    </MessageExpandable>
  );

  const renderResultBody = () => {
    // Running out of credits is a recoverable stop, not a failure: show the
    // ways forward (subscribers get Arya-free / buy / Premium + refresh date;
    // free users get the out-of-credits modal) instead of a red error line.
    if (isCreditFailure) {
      return (
        <View style={styles.creditErrorBox}>
          <View style={styles.creditErrorHeader}>
            <View style={styles.creditErrorIcon}>
              <Icon name='coins' size={15} color={colors.accent_color_primary} solid />
            </View>
            <View style={{ flex: 1 }}>
              <Text size='n' weight='semibold' color='primary'>
                {isPlus
                  ? t('plusOutOfCredits.inlineTitle')
                  : (t('messages.creditsDepleted.outOfCredits') || 'You\u2019re out of credits')}
              </Text>
              <Text size='s' color='secondary'>
                {t('pages.tools.notChargedForFailure') || 'You were not charged for this run.'}
              </Text>
            </View>
          </View>
          {isPlus ? (
            <PlusOutOfCreditsOptions source='tool_run' />
          ) : (
            <Button
              variant='primary'
              size='small'
              prependIcon='coins'
              text={t('pages.tools.getCredits') || 'Get credits'}
              onPress={() => openModal('OutOfCreditsModal', { source: 'tool_run' })}
            />
          )}
        </View>
      );
    }

    if (isFailed) {
      return (
        <View>
          <View style={styles.errorBox}>
            <Text color='danger' size='n'>{run?.error?.message || t('pages.tools.runFailed') || 'Tool run failed'}</Text>
          </View>
          <ToolModelRetry tool={tool} run={run} onRetry={onRetryWithModel} disabled={isRetryDisabled} />
        </View>
      );
    }

    return (
      <>
        {hasResultSegments ? (
          <View style={styles.segmentsColumn}>
            {resultSegments.map((seg, idx) => {
              const modelName = seg?.model?.name || seg?.model?.slug || (t('pages.tools.modelResponse') || 'Response');
              const body = typeof seg?.content === 'string' ? seg.content : '';
              const segMessageId = runMessageId ? `${runMessageId}-seg-${idx}` : `tool-seg-${idx}`;
              return (
                <View key={segMessageId} style={styles.segmentCard}>
                  <View style={styles.segmentHeader}>
                    <Text size='s' weight='bold' color='primary' numberOfLines={1} style={{ flex: 1, minWidth: 0 }}>
                      {modelName}
                    </Text>
                  </View>
                  {body.trim() ? (
                    renderToolTextOutput(body, segMessageId, colors.solid_color_tertiary)
                  ) : (
                    <Text size='xs' color='secondary'>{t('pages.tools.segmentNoText') || 'No text for this model.'}</Text>
                  )}
                </View>
              );
            })}
          </View>
        ) : !!resultText ? (
          renderToolTextOutput(resultText, runMessageId, colors.solid_color_block)
        ) : null}

        {!!resultFiles.length && (
          <View style={styles.fileGrid}>
            {renderResultFiles(resultFiles, run, styles, colors, openModal)}
          </View>
        )}

        {!hasResultSegments && !resultText && !resultFiles.length && (
          <Text size='n' color='secondary'>{t('pages.tools.noOutput') || 'No output produced.'}</Text>
        )}
      </>
    );
  };

  const showActions = !isRunning && !isFailed;
  const actionItems = showActions ? [
    {
      key: 'continue',
      icon: 'message',
      label: isPromoting
        ? (t('pages.tools.openingInChat') || 'Opening…')
        : (t('pages.tools.continueInChat') || 'Continue in chat'),
      onPress: handleContinueInChat,
      disabled: isPromoting,
      primary: true,
    },
    !!copyPlainText?.trim() && {
      key: 'copy',
      icon: 'copy',
      label: t('pages.tools.copy') || 'Copy',
      onPress: handleCopy,
    },
    downloadableFiles.length > 0 && (downloadableFiles[0]?.id || downloadableFiles[0]?._id || downloadableFiles[0]?.fileId) && {
      key: 'open',
      icon: 'arrow-up-right-from-square',
      // FileEditModal is single-file by design. The chip targets the
      // *primary* (first) output for one-tap access; for multi-file runs
      // each file's caption row is also a Pressable that opens its own
      // FileEditModal, so users can reach everything without us cramming
      // a separate chip per file.
      label: t('pages.tools.openFile') || 'Open file',
      onPress: handleOpen,
    },
    // Native share sheet — only on iOS / Android, and only when there's
    // something fetchable to share. We deliberately put this BEFORE Download
    // in the row so it's the first non-primary action a touch user sees:
    // sharing-to-Messages is a far more common destination on a phone than
    // saving a copy to disk. The Share chip never appears on web because
    // web's "share" verb is a download-then-attach flow that's already
    // covered by the Download chip.
    IS_TOUCH_NATIVE && downloadableFiles.length > 0 && {
      key: 'share',
      icon: isSharing ? 'circle-notch' : 'share-from-square',
      label: isSharing
        ? (t('pages.tools.sharing') || 'Sharing…')
        : (t('pages.tools.share') || 'Share'),
      onPress: handleShare,
      disabled: isSharing,
      spinning: isSharing,
    },
    downloadableFiles.length > 0 && {
      key: 'download',
      icon: isDownloading ? 'circle-notch' : (IS_TOUCH_NATIVE && hasPhotoLibraryMedia ? 'photo-film' : 'download'),
      // On iOS / Android the download path actually saves through
      // MediaLibrary into the system Photos app (see saveToPhotos in
      // mediaDownload.js). Calling that "Download" reads as wrong-platform
      // copy and confuses users hunting for a Files-app destination — match
      // the verb to the destination.
      label: isDownloading
        ? (IS_TOUCH_NATIVE && hasPhotoLibraryMedia
          ? (t('pages.tools.savingToPhotos') || 'Saving…')
          : (t('pages.tools.downloading') || 'Downloading…'))
        : (downloadableFiles.length > 1
          ? (IS_TOUCH_NATIVE && hasPhotoLibraryMedia
            ? (t('pages.tools.saveCount') || 'Save (%COUNT%)').replace('%COUNT%', downloadableFiles.length)
            : (t('pages.tools.downloadCount') || 'Download (%COUNT%)').replace('%COUNT%', downloadableFiles.length))
          : (IS_TOUCH_NATIVE && hasPhotoLibraryMedia
            ? (t('pages.tools.saveToPhotos') || 'Save to Photos')
            : (t('pages.tools.download') || 'Download'))),
      onPress: handleDownload,
      disabled: isDownloading,
      spinning: isDownloading,
    },
    onEditRerun && {
      key: 'rerun',
      icon: 'rotate-right',
      label: t('pages.tools.editAndRerun') || 'Edit & re-run',
      onPress: () => onEditRerun(run),
    },
  ].filter(Boolean) : [];

  const renderActions = (placement) => (
    <View
      style={[
        styles.actions,
        placement === 'top' ? styles.actionsTop : styles.actionsBottom,
      ]}
    >
      {actionItems.map(item => (
        <ActionChip key={item.key} item={item} styles={styles} colors={colors} />
      ))}
    </View>
  );

  return (
    <View style={styles.container}>
      {showActions && actionItems.length > 0 && renderActions('top')}

      <View style={styles.body}>{renderResultBody()}</View>

      {showActions && actionItems.length > 0 && renderActions('bottom')}

      {(() => {
        if (isRunning) return null;
        const usedModels = Array.isArray(run?.modelsUsed) ? run.modelsUsed : [];
        const modelNames = usedModels.map(m => m?.name).filter(Boolean);
        const hasMeta = modelNames.length > 0 || run?.creditCost != null;
        if (!hasMeta) return null;
        const modelLabel = modelNames.length === 0
          ? null
          : modelNames.length === 1
            ? (t('pages.tools.modelLabel') || 'Model: %NAME%').replace('%NAME%', modelNames[0])
            : (t('pages.tools.modelsLabel') || 'Models: %NAMES%').replace('%NAMES%', modelNames.join(', '));
        return (
          <View style={styles.meta}>
            {modelLabel && (
              <Text size='xs' color='secondary'>{modelLabel}</Text>
            )}
            {run?.creditCost != null && (
              <Text size='xs' color='secondary'>
                {(t('pages.tools.creditsUsedLabel') || 'Credits used: %COUNT%').replace('%COUNT%', run.creditCost)}
              </Text>
            )}
          </View>
        );
      })()}
    </View>
  );
}

function ActionChip({ item, styles, colors }) {
  // Spinner takes priority over the static icon when an action is in flight,
  // so the download / continue-in-chat chips give live feedback instead of
  // looking dead while a request is mid-flight.
  const showSpinner = item.spinning || (item.disabled && item.primary);
  return (
    <Pressable
      onPress={item.disabled ? undefined : item.onPress}
      disabled={item.disabled}
      accessibilityRole='button'
      accessibilityLabel={item.label}
      style={({ hovered }) => [
        styles.chip,
        item.primary && styles.chipPrimary,
        hovered && !item.disabled && (item.primary ? styles.chipPrimaryHovered : styles.chipHovered),
        item.disabled && styles.chipDisabled,
      ]}
    >
      {showSpinner
        ? <ActivityIndicator size='small' color={colors.text_color_primary} style={{ width: 13, height: 13 }} />
        : <Icon name={item.icon} size={12} color={colors.text_color_primary} />}
      <Text size='s' weight='medium' color='primary'>{item.label}</Text>
    </Pressable>
  );
}

function renderResultFiles(resultFiles, run, styles, colors, openModal) {
  const openInModal = (file) => {
    const fid = file?.id || file?._id || file?.fileId;
    if (!fid || typeof openModal !== 'function') return;
    openModal('FileEditModal', { fileId: String(fid) });
  };
  const tool = run?.tool || {};
  const operation = tool?.operation || run?.toolSlug || '';
  const outputType = tool?.outputType || '';

  if (operation.includes('layer') && resultFiles.length > 1) {
    return <LayerViewer files={resultFiles} />;
  }

  return resultFiles.map((file, i) => {
    const mimeType = file?.mimeType || '';
    const url = file?.url || file?.path || file?.signedUrl || file?.cdnPath;
    const extension = getFileExtension(file);
    const isImage = mimeType.startsWith('image/');
    const isVideo = mimeType.startsWith('video/');
    const isAudio = mimeType.startsWith('audio/');
    const isPointCloud = operation.includes('point-cloud') || ['ply', 'pcd', 'xyz', 'las', 'laz'].includes(extension);
    const is3D = outputType === '3d-model' || ['glb', 'gltf', 'obj', 'stl', 'fbx'].includes(extension);

    if (isPointCloud) {
      return (
        <View key={file.id || i} style={styles.mediaItem}>
          <PointCloudViewer file={file} />
          <FileCaption file={file} fallback='Point cloud' styles={styles} colors={colors} onPress={() => openInModal(file)} />
        </View>
      );
    }

    if (is3D) {
      // 3D files keep their textures inside `metadata['3d']` per the File
      // schema (alternateFormats + USDZ for iOS Quick Look). Reading those
      // here means OBJ/Hunyuan results actually pick up MTL + base-color
      // texture URLs the way ExploreLightbox / Model3DCardContent do, so
      // the in-app preview matches the downloaded asset instead of falling
      // back to the gray "no material" path.
      const meta3d = file?.metadata?.['3d'] || file?.metadata?.threed || {};
      const alternateFormats = Array.isArray(meta3d.alternateFormats) ? meta3d.alternateFormats : [];
      const usdzAlt = alternateFormats.find(f => (f?.format || '').toLowerCase() === 'usdz');
      const iosSrc = usdzAlt?.url || null;
      const posterUrl = file?.thumbnailPath || file?.thumbnailUrl || file?.previewUrl || null;
      return (
        <View key={file.id || i} style={styles.mediaItem}>
          <View style={styles.modelFrame}>
            <Model3DViewer
              modelUrl={url}
              posterUrl={posterUrl}
              iosSrc={iosSrc}
              alternateFormats={alternateFormats}
              autoRotate
            />
          </View>
          <FileCaption file={file} fallback='3D model' styles={styles} colors={colors} onPress={() => openInModal(file)} />
        </View>
      );
    }

    if (isVideo && url && Platform.OS === 'web') {
      return (
        <View key={file.id || i} style={styles.mediaItem}>
          <View style={styles.mediaPreview}>
            {/* eslint-disable-next-line react/forbid-elements */}
            <video controls src={url} style={{ width: '100%', borderRadius: 8 }} />
          </View>
          <FileCaption file={file} fallback='Video' styles={styles} colors={colors} onPress={() => openInModal(file)} />
        </View>
      );
    }

    if (isAudio && url) {
      // Native: use the same expo-audio waveform player chat uses for audio
      // messages, so generated music / TTS / sound-effect output is playable
      // inline (instead of falling through to a "tap to open in modal" tile).
      // Web keeps the lighter native <audio controls> element since the
      // browser already provides a polished default UI and the extra player
      // bundle isn't worth the bytes there.
      if (Platform.OS !== 'web') {
        return (
          <View key={file.id || i} style={styles.mediaItem}>
            <View style={styles.audioPlayerWrap}>
              <AudioWaveformPlayer
                sourceUrl={url}
                file={file}
                size={320}
              />
            </View>
            <FileCaption file={file} fallback='Audio' styles={styles} colors={colors} onPress={() => openInModal(file)} />
          </View>
        );
      }
      return (
        <View key={file.id || i} style={styles.mediaItem}>
          <View style={styles.mediaPreview}>
            {/* eslint-disable-next-line react/forbid-elements */}
            <audio controls src={url} style={{ width: '100%' }} />
          </View>
          <FileCaption file={file} fallback='Audio' styles={styles} colors={colors} onPress={() => openInModal(file)} />
        </View>
      );
    }

    if (isImage && url) {
      const previewAspect = resolveToolImagePreviewAspectRatio(file, run);
      return (
        <View key={file.id || i} style={[styles.mediaItem, { width: '100%', maxWidth: 280 }]}>
          <Pressable
            onPress={() => openInModal(file)}
            style={[styles.imageThumbWrap, styles.imagePreviewFrame, { aspectRatio: previewAspect }]}
          >
            <Image source={{ uri: url }} style={styles.image} resizeMode="cover" />
          </Pressable>
          <FileCaption file={file} fallback='Image' styles={styles} colors={colors} onPress={() => openInModal(file)} />
        </View>
      );
    }
    return (
      <Pressable key={file.id || i} onPress={() => openInModal(file)} style={styles.fileTile}>
        <Icon name='file' size={16} color={colors.text_color_secondary} />
        <Text size='s' color='primary' numberOfLines={1}>
          {file?.originalName || file?.name || 'File'}
        </Text>
      </Pressable>
    );
  });
}

// Small caption row rendered under every media preview so users can tell
// outputs apart in multi-file runs and see the file extension at a glance.
// Tapping the caption opens the shared FileEditModal for that specific file.
function FileCaption({ file, fallback, styles, colors, onPress }) {
  const name = file?.originalName || file?.name || fallback || 'File';
  const fid = file?.id || file?._id || file?.fileId;
  const canOpen = !!fid && typeof onPress === 'function';
  return (
    <Pressable
      onPress={canOpen ? onPress : undefined}
      disabled={!canOpen}
      accessibilityRole={canOpen ? 'button' : undefined}
      accessibilityLabel={canOpen ? `Open ${name}` : undefined}
      style={({ hovered }) => [
        styles.mediaCaption,
        canOpen && hovered && Platform.OS === 'web' && { opacity: 0.75 },
      ]}
    >
      <Icon name='file' size={11} color={colors.text_color_tertiary} style={styles.mediaCaptionIcon} />
      <Text size='xs' color='secondary' numberOfLines={1} style={styles.mediaCaptionText}>
        {name}
      </Text>
      {canOpen && (
        <Icon name='arrow-up-right-from-square' size={9} color={colors.text_color_tertiary} />
      )}
    </Pressable>
  );
}

function getFileExtension(file) {
  const raw = file?.originalName || file?.name || file?.url || file?.path || '';
  const clean = String(raw).split('?')[0].split('#')[0];
  const ext = clean.includes('.') ? clean.split('.').pop() : '';
  return String(ext || '').toLowerCase();
}

// Best-effort URL extractor for serialized File objects on a ToolRun. The
// shape varies across providers — older code paths use `cdnPath`, newer
// ones may pass a presigned `signedUrl` or a fully-formed `url`. We try the
// most-direct keys first so the download button works without needing the
// caller to normalize the file shape upstream.
function fileUrlOf(file) {
  if (!file || typeof file !== 'object') return null;
  return file.url || file.signedUrl || file.cdnPath || file.path || null;
}

export default React.memo(ToolResultDisplay);
